import { ScenarioConfig } from './scenarioConfig';
import {
  ScenarioInvalidJobIdError,
  ScenarioJobAlreadyPollingError,
  ScenarioJobTimeoutError,
} from './scenarioErrors';

import type { ScenarioJobResponse, ScenarioModel3DJobResponse } from './scenarioTypes';

/** Progress callback for job polling */
export type ProgressCallback = (progress: number, status: string) => void;

/** Result from a completed job */
export interface JobResult {
  jobId: string;
  images: string[]; // Asset IDs
  status: 'success' | 'failure';
  /** Error message from the API when status is 'failure' */
  error?: string;
}

/** Debug flags for job poller */
const DEBUG = {
  polling: false,
} as const;

/**
 * Job polling service for Scenario API
 * Handles polling for both standard and 3D generation jobs
 */
export class JobPoller {
  private activePollJobs = new Set<string>();

  constructor(private getJob: (jobId: string) => Promise<ScenarioJobResponse | ScenarioModel3DJobResponse>) { }

  /**
   * Validate job ID before polling
   */
  private validateJobId(jobId: string | undefined): void {
    if (!jobId || jobId === 'undefined' || jobId === 'null') {
      throw new ScenarioInvalidJobIdError(jobId);
    }
  }

  /**
   * Check if a job is already being polled
   */
  private ensureNotPolling(jobId: string): void {
    if (this.activePollJobs.has(jobId)) {
      throw new ScenarioJobAlreadyPollingError(jobId);
    }
  }

  /**
   * Extract job data from response (handles different response structures)
   */
  private extractJobData(response: any): {
    status: string;
    progress: number;
    assetIds: string[];
    error?: string;
  } {
    // Response has job data nested under 'job' property or directly on the response
    const job = response.job ?? response;

    return {
      status: job.status ?? 'pending',
      progress: Math.round((job.progress ?? 0) * 100), // Convert 0-1 to 0-100
      assetIds: job.metadata?.assetIds ?? [],
      error: job.error,
    };
  }

  /**
   * Poll standard job status until completion with progress tracking
   */
  async poll(jobId: string, onProgress?: ProgressCallback): Promise<JobResult> {
    this.validateJobId(jobId);
    this.ensureNotPolling(jobId);

    this.activePollJobs.add(jobId);
    if (DEBUG.polling) console.log(`🔄 Starting polling for job: ${jobId}`);

    try {
      const maxAttempts = ScenarioConfig.polling.maxAttempts;
      const intervalMs = ScenarioConfig.polling.intervalMs;
      let attempts = 0;

      while (attempts < maxAttempts) {
        try {
          const jobResponse = await this.getJob(jobId);
          const jobData = this.extractJobData(jobResponse);

          // Only log status changes or every 10th attempt to reduce spam
          if (
            DEBUG.polling &&
            (attempts === 0 ||
              attempts % 10 === 0 ||
              jobData.status === 'success' ||
              jobData.status === 'failure')
          ) {
            console.log(
              `📊 Job ${jobId} - Status: ${jobData.status}, Progress: ${jobData.progress}%, Attempt: ${attempts + 1}`
            );
          }

          // Report progress to callback
          if (onProgress) {
            const statusText =
              jobData.status === 'pending'
                ? 'Queued'
                : jobData.status === 'in-progress'
                  ? `Generating... ${jobData.progress}%`
                  : jobData.status === 'success'
                    ? 'Completed'
                    : jobData.status;
            onProgress(jobData.progress, statusText);
          }

          if (jobData.status === 'success') {
            return {
              jobId,
              images: jobData.assetIds,
              status: 'success',
            };
          }

          if (jobData.status === 'failure' || jobData.status === 'failed') {
            const errorMsg = jobData.error ?? 'Job failed with no error details from API';
            console.error(`❌ Job ${jobId} failed:`, errorMsg, 'Full job data:', jobData);
            return {
              jobId,
              images: [],
              status: 'failure',
              error: errorMsg,
            };
          }

          // Wait before next poll
          await new Promise(resolve => setTimeout(resolve, intervalMs));
          attempts++;
        } catch (error) {
          console.error('Error polling job status:', error);
          attempts++;
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      }

      throw new ScenarioJobTimeoutError(jobId, maxAttempts);
    } finally {
      this.activePollJobs.delete(jobId);
      if (DEBUG.polling) console.log(`🏁 Finished polling for job: ${jobId}`);
    }
  }

  /**
   * Poll 3D job status until completion with progress tracking
   * 3D jobs have different response structure and longer timeouts
   */
  async poll3D(jobId: string, onProgress?: ProgressCallback): Promise<JobResult> {
    this.validateJobId(jobId);
    this.ensureNotPolling(jobId);

    this.activePollJobs.add(jobId);
    if (DEBUG.polling) console.log(`🔄 Starting 3D polling for job: ${jobId}`);

    try {
      const maxAttempts = ScenarioConfig.polling.max3DAttempts;
      const intervalMs = ScenarioConfig.polling.intervalMs;
      let attempts = 0;

      while (attempts < maxAttempts) {
        try {
          const jobStatus = (await this.getJob(jobId)) as ScenarioModel3DJobResponse;

          if (DEBUG.polling) {
            console.log(`🔄 3D Job ${jobId} attempt ${attempts + 1}/${maxAttempts}:`, {
              status: jobStatus.job.status,
              progress: jobStatus.job.progress,
            });
          }

          // Update progress if callback provided
          if (onProgress && jobStatus.job.progress !== undefined) {
            const statusText =
              jobStatus.job.status === 'processing'
                ? 'Generating 3D model...'
                : jobStatus.job.status === 'queued'
                  ? 'Queued...'
                  : jobStatus.job.status;
            onProgress(jobStatus.job.progress, statusText);
          }

          if (jobStatus.job.status === 'success') {
            const assetIds = jobStatus.job.metadata?.assetIds ?? [];
            if (assetIds.length === 0) {
              console.warn('⚠️ Job completed successfully but no asset IDs returned');
            }

            return {
              jobId,
              images: assetIds,
              status: 'success',
            };
          }

          if (jobStatus.job.status === 'failed' || jobStatus.job.status === 'canceled') {
            const errorMsg = jobStatus.job.error ?? `Job ${jobStatus.job.status} with no error details from API`;
            console.error(`❌ 3D generation ${jobStatus.job.status} for job ${jobId}:`, errorMsg);
            return {
              jobId,
              images: [],
              status: 'failure',
              error: errorMsg,
            };
          }

          // Continue polling for 'queued' and 'processing' states
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, intervalMs));
          }
        } catch (error) {
          console.error(`❌ Error polling 3D job ${jobId} on attempt ${attempts + 1}:`, error);

          // For network errors, retry a few times before giving up
          if (attempts >= 3) {
            throw error;
          }

          attempts++;
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      }

      // If we've exhausted all attempts
      const timeoutError = `3D generation timed out after ${maxAttempts} polling attempts (job ${jobId})`;
      console.error(`❌ ${timeoutError}`);
      return {
        jobId,
        images: [],
        status: 'failure',
        error: timeoutError,
      };
    } finally {
      this.activePollJobs.delete(jobId);
      if (DEBUG.polling) console.log(`🏁 Finished 3D polling for job: ${jobId}`);
    }
  }

  /**
   * Check if a job is currently being polled
   */
  isPolling(jobId: string): boolean {
    return this.activePollJobs.has(jobId);
  }

  /**
   * Get count of active polling jobs
   */
  getActivePollCount(): number {
    return this.activePollJobs.size;
  }
}

